import {announce} from '@github-ui/aria-live'
import {PercentageCircle} from '@github-ui/percentage-circle'
import {useQuery} from '@github-ui/react-query'
// eslint-disable-next-line no-restricted-imports
import {useToastContext} from '@github-ui/toast/ToastContext'
import {ClockIcon} from '@primer/octicons-react'
import type {ReactNode} from 'react'
import {useEffect, useRef} from 'react'

import {fetchIssuesBulkJobStatus, ISSUES_BULK_UPDATE_TIMEOUT_MESSAGE, ISSUES_BULK_UPDATE_TIMEOUT_MS} from './job-status'

const INITIAL_POLL_INTERVAL_MS = 5_000
const MAX_POLL_INTERVAL_MS = 60_000

type JobInfoProps = {
  children: ReactNode | null
  bulkJobId: string | null
  setBulkJobId: (bulkJobId: string | null) => void
  onSuccess?: (() => void) | null
}

export function JobInfo({children, bulkJobId, setBulkJobId, onSuccess}: JobInfoProps) {
  const {addPersistedToast, addToast, clearPersistedToast} = useToastContext()
  const handledJobIdsRef = useRef(new Set<string>())

  const jobQuery = useQuery({
    enabled: Boolean(bulkJobId),
    queryKey: ['issues-bulk-job', bulkJobId],
    queryFn: ({signal}) => fetchIssuesBulkJobStatus({jobId: bulkJobId ?? '', signal}),
    retry: true,
    refetchInterval: query => {
      if (
        query.state.data?.status === 'completed' ||
        query.state.data?.status === 'failed' ||
        query.state.data?.status === 'missing'
      ) {
        return false
      }
      return Math.min(
        INITIAL_POLL_INTERVAL_MS * 1.5 ** Math.max(query.state.dataUpdateCount - 1, 0),
        MAX_POLL_INTERVAL_MS,
      )
    },
  })

  useEffect(() => {
    if (!bulkJobId || handledJobIdsRef.current.has(bulkJobId)) return

    const timeout = setTimeout(() => {
      handledJobIdsRef.current.add(bulkJobId)
      setBulkJobId(null)
      clearPersistedToast()
      // eslint-disable-next-line @github-ui/dotcom-primer/toast-migration
      addToast({type: 'error', message: ISSUES_BULK_UPDATE_TIMEOUT_MESSAGE})
      announce(ISSUES_BULK_UPDATE_TIMEOUT_MESSAGE, {assertive: true})
    }, ISSUES_BULK_UPDATE_TIMEOUT_MS)

    return () => clearTimeout(timeout)
  }, [addToast, bulkJobId, clearPersistedToast, setBulkJobId])

  useEffect(() => {
    if (!bulkJobId || handledJobIdsRef.current.has(bulkJobId)) {
      if (!bulkJobId) clearPersistedToast()
      return
    }

    const status = jobQuery.data
    if (!status) return

    if (status.status === 'missing' || status.status === 'failed') {
      handledJobIdsRef.current.add(bulkJobId)
      setBulkJobId(null)
      clearPersistedToast()
      // eslint-disable-next-line @github-ui/dotcom-primer/toast-migration
      addToast({
        type: 'error',
        message: status.status === 'failed' ? status.errorMessage : 'Bulk update had an error',
      })
      return
    }

    if (status.status === 'pending') {
      if (status.progress.state === 'STARTED') {
        const percentage = Math.min(Math.max(status.progress.percentage, 0), 100)
        // eslint-disable-next-line @github-ui/dotcom-primer/toast-migration
        addPersistedToast({
          message: `Bulk update is in progress ${percentage} % ${
            status.progress.failedCount > 0 ? `(${status.progress.failedCount} issues failing to update)` : ''
          }`,
          icon: <PercentageCircle progress={percentage / 100} />,
          type: 'info',
          role: 'status',
        })
      } else {
        // eslint-disable-next-line @github-ui/dotcom-primer/toast-migration
        addPersistedToast({message: 'Bulk update is pending', icon: <ClockIcon />, type: 'info', role: 'status'})
      }
      return
    }

    handledJobIdsRef.current.add(bulkJobId)
    const message = `Bulk update completed ${
      status.result.failedIssueNodeIds.length > 0 ? 'with errors' : 'successfully'
    }`
    setBulkJobId(null)
    clearPersistedToast()
    // eslint-disable-next-line @github-ui/dotcom-primer/toast-migration
    addToast({type: 'success', message})
    announce(message)
    onSuccess?.()
  }, [addPersistedToast, addToast, bulkJobId, clearPersistedToast, jobQuery.data, onSuccess, setBulkJobId])

  return <>{children}</>
}
